import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { GTMPageBlockTrackingModel } from '@libs/shared/models';
import { PageBlockImpressionTrackerService } from './page-block-impression-tracker.service';

@Injectable({
  providedIn: 'root',
})
export class PageBlockImpressionObserverService {
  private readonly platformId: string = inject<string>(PLATFORM_ID);
  private readonly intersectionThreshold: number = 0.8;
  private readonly elementImpressionsMap: Map<Element, GTMPageBlockTrackingModel | undefined> = new Map<Element, GTMPageBlockTrackingModel | undefined>();
  private readonly intersectionObserver?: IntersectionObserver;

  public constructor(
    private readonly impressionTracker: PageBlockImpressionTrackerService,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.intersectionObserver = new IntersectionObserver(
        this.handleIntersectionEntries.bind(this),
        { threshold: this.intersectionThreshold },
      );
    }
  }

  public observeElement(element: Element, impression: GTMPageBlockTrackingModel | undefined): void {
    this.elementImpressionsMap.set(element, impression);

    const observer: IntersectionObserver | undefined = this.intersectionObserver;

    if (!observer) {
      return;
    }

    // Delay observing to ensure dynamic components (e.g. sliders) have fully stabilized layout
    window.setTimeout(() => {
      observer.observe(element);
    }, 500);
  }

  private handleIntersectionEntries(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        this.trackImpression(entry.target);
      }
    });
  }

  private trackImpression(element: Element): void {
    const impression: GTMPageBlockTrackingModel | undefined = this.elementImpressionsMap.get(element);

    //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.intersectionObserver!.unobserve(element);
    this.elementImpressionsMap.delete(element);

    if (!impression) {
      return;
    }

    this.impressionTracker.track(impression);
  }
}
